import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { IconChip } from './IconChip.js';

/**
 * EmptyState — nothing here, and why.
 *
 * ── WHY THIS IS NOT COSMETIC ──────────────────────────────────────────────
 *
 * In an accounting product, empty and zero are different claims, and confusing
 * them is expensive. A trial balance with no rows is not "balanced"; a set of
 * integrity checks over an empty ledger passes vacuously — nine true statements
 * about no vouchers — and rendering that as a green tick tells a tenant with no
 * books that their books are sound.
 *
 * So an empty state has to say WHICH kind of empty it is:
 *
 *   nothing yet   — this has never had data. Usually followed by an action.
 *   filtered out  — there is data, this filter excludes it. Followed by "clear".
 *   not available — real data exists but cannot be shown (no permission, not
 *                   connected). Followed by an explanation, not an action.
 *
 * `reason` is required for exactly that: it forces the caller to decide which
 * of the three they mean instead of defaulting to a shrug.
 */

export type EmptyReason = 'nothing-yet' | 'filtered-out' | 'not-available';

export function EmptyState({
  reason,
  icon,
  title,
  description,
  action,
  className,
  compact = false,
}: {
  reason: EmptyReason;
  icon?: (props: { size?: number }) => ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** For an empty state inside a card body rather than a whole page. */
  compact?: boolean;
}) {
  return (
    <div
      data-reason={reason}
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'gap-2 px-6 py-10' : 'gap-3 px-6 py-16',
        className,
      )}
    >
      {icon && <IconChip icon={icon} size="lg" intent="neutral" />}
      <p className={cn('font-semibold text-primary', compact ? 'text-md' : 'text-lg')}>{title}</p>
      {description && (
        <p className="max-w-[52ch] text-md leading-snug text-tertiary">{description}</p>
      )}
      {action && <div className="mt-2 flex items-center gap-2">{action}</div>}
    </div>
  );
}
